import type { ReactNode } from 'react';
import { ResearchCard, type ResearchFinding } from './ResearchCard';
import { VideoGallery } from './VideoGallery';
import { CollapsibleCard } from '../primitives/CollapsibleCard';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { ArrowDown, BarChart3, CalendarDays, Check, ClipboardList, DollarSign, Mail, X } from '../icons';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, EASE, FONT_MONO, SURFACE, TEXT_SECONDARY } from '../../tokens';

interface BeforeAfterState {
  time: string;
  result: string;
  detail: string;
}

interface BeforeAfter {
  scenario: string;
  icon: ReactNode;
  before: BeforeAfterState;
  after: BeforeAfterState;
}

const BEFORE_AFTER: readonly BeforeAfter[] = [
  {
    scenario: 'Lead Response',
    icon: <Mail size={22} strokeWidth={1.5} />,
    before: { time: 'Hours, sometimes days', result: 'Contact odds fall away', detail: 'The form fills while you are with a client. You get to it tonight.' },
    after: { time: 'Under a minute', result: 'Booked while intent is live', detail: 'Qualified in conversation, booking link sent, before it cools.' },
  },
  {
    scenario: 'Reviews',
    icon: <CalendarDays size={22} strokeWidth={1.5} />,
    before: { time: 'When you remember', result: 'Thin social proof', detail: 'You ask the happy ones, sometimes, if it is not awkward.' },
    after: { time: 'Every completed job', result: 'Asked every time', detail: 'Fires on completion, on the channel that customer actually uses.' },
  },
  {
    scenario: 'Posting Cadence',
    icon: <ClipboardList size={22} strokeWidth={1.5} />,
    before: { time: 'Whenever there is time', result: 'Reads as inactive', detail: 'Three posts in a week, then nothing for a month.' },
    after: { time: '43 pieces a month', result: 'Known as active', detail: 'Queued ahead across every channel, waiting on your approval.' },
  },
  {
    scenario: 'Objection Handling',
    icon: <BarChart3 size={22} strokeWidth={1.5} />,
    before: { time: 'Repeated on every call', result: 'Same five questions', detail: 'You answer them one prospect at a time, forever.' },
    after: { time: 'Answered before the call', result: 'They arrive convinced', detail: 'Mined from your real calls, published as content, resolved in advance.' },
  },
  {
    scenario: 'Meta Ads',
    icon: <DollarSign size={22} strokeWidth={1.5} />,
    before: { time: 'Boosted and hoped', result: 'No idea what worked', detail: 'Spend goes out, results come back, nothing explains the gap.' },
    after: { time: 'Weekly attribution', result: 'Repeatable on purpose', detail: 'Meta runs against the same content and footage, wins traced to a cause.' },
  },
];

const RESEARCH: readonly ResearchFinding[] = [
  {
    source: 'Oldroyd, MIT Sloan / InsideSales.com, 2007',
    finding: 'Contact odds drop more than tenfold within the first hour of a lead coming in, and the odds of qualifying that lead drop sharply between the 5 and 30 minute mark.',
  },
  {
    source: 'Trope and Liberman, 2010',
    finding: 'Temporal distance changes how a mind processes the same task. A lead sitting a few minutes flips from concrete and urgent to abstract and can-wait, for the prospect and for how they now see the business.',
  },
  {
    source: 'BrightLocal, 2026 edition',
    finding: '97% of consumers read reviews before choosing a local business, which makes review collection part of the funnel rather than something that happens after it.',
  },
  {
    source: 'Deutsch and Gerard, 1955',
    finding: 'When people have no direct evidence of their own, they treat the judgments of others as evidence about reality. Cialdini names the same mechanism social proof.',
  },
  {
    source: 'Cialdini, commitment and consistency',
    finding: 'Someone who resolves an objection themselves while reading holds to that resolution harder than someone who was simply told the answer. Objection-led content is a self-persuasion event, not a pitch.',
  },
  {
    source: 'Whillans, Weidman and Dunn, 2016',
    finding: 'People who prioritise time over money are measurably happier than those who prioritise money over time, independent of how much money they actually have. I am selling back hours, not dollars.',
  },
];

export function ProofSection() {
  const isMobile = useIsMobile();

  return (
    <section
      id="proof"
      style={{ padding: isMobile ? '60px 20px' : '100px 64px', background: 'rgba(6,6,6,0.88)' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <SectionLabel number="04">Proof Over Words</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>Every one of these is a decision already made.</SectionH2>
            <p style={{ color: TEXT_SECONDARY, maxWidth: 560, margin: '0 auto', lineHeight: 1.75 }}>
              Not opinions about marketing. The mechanisms underneath, and what changes when each one
              stops depending on you remembering to do it.
            </p>
          </div>
        </FadeUp>

        {isMobile && (
          <div
            style={{
              textAlign: 'center',
              marginBottom: 10,
              fontFamily: FONT_MONO,
              fontSize: '0.6rem',
              color: '#666',
              letterSpacing: '0.08em',
            }}
          >
            <span style={{ color: ACCENT }}>←</span> swipe <span style={{ color: ACCENT }}>→</span>
          </div>
        )}

        <div
          className="no-scrollbar"
          style={{
            display: 'flex',
            gap: 18,
            overflowX: 'auto',
            overflowY: 'hidden',
            overscrollBehaviorX: 'contain',
            scrollSnapType: 'x mandatory',
            paddingTop: 14,
            paddingBottom: 28,
            marginBottom: 36,
          }}
        >
          {BEFORE_AFTER.map((item, idx) => (
            <FadeUp key={item.scenario} delay={idx * 60}>
              <div
                style={{
                  flex: isMobile ? '0 0 min(280px, 82vw)' : '0 0 220px',
                  scrollSnapAlign: 'start',
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 20,
                  overflow: 'hidden',
                  transition: `border-color 0.3s, transform 0.3s ${EASE}`,
                  height: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,107,0,0.2)';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = BORDER;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div
                  style={{
                    padding: '16px 22px',
                    borderBottom: `1px solid ${BORDER}`,
                    background: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div style={{ color: ACCENT, marginBottom: 8 }}>{item.icon}</div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: '0.7rem', color: ACCENT, letterSpacing: '0.08em' }}>
                    {item.scenario.toUpperCase()}
                  </div>
                </div>

                <div style={{ padding: '20px 22px' }}>
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: '0.58rem',
                        color: '#555',
                        letterSpacing: '0.1em',
                        marginBottom: 7,
                        textTransform: 'uppercase',
                      }}
                    >
                      Before
                    </div>
                    <div style={{ fontSize: '1rem', lineHeight: 1.6 }}>
                      <strong style={{ color: '#666' }}>{item.before.time}</strong> - {item.before.detail}
                      <br />
                      <span
                        style={{
                          color: '#f87171',
                          fontSize: '0.8rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <X size={14} strokeWidth={3} /> {item.before.result}
                      </span>
                    </div>
                  </div>

                  <div
                    aria-hidden="true"
                    style={{
                      textAlign: 'center',
                      color: ACCENT,
                      marginBottom: 14,
                      fontSize: '1.1rem',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowDown size={20} />
                  </div>

                  <div>
                    <div
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: '0.58rem',
                        color: ACCENT,
                        letterSpacing: '0.1em',
                        marginBottom: 7,
                        textTransform: 'uppercase',
                      }}
                    >
                      After Augment OS
                    </div>
                    <div style={{ fontSize: '1rem', color: TEXT_SECONDARY, lineHeight: 1.6 }}>
                      <strong style={{ color: ACCENT }}>{item.after.time}</strong> - {item.after.detail}
                      <br />
                      <span
                        style={{
                          color: '#4ade80',
                          fontSize: '0.8rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Check size={14} strokeWidth={2.5} /> {item.after.result}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <VideoGallery isMobile={isMobile} />

        <FadeUp delay={80}>
          <div style={{ marginTop: 36 }}>
            <CollapsibleCard title="What the Research Says">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? 16 : 20,
                }}
              >
                {RESEARCH.map((r) => (
                  <ResearchCard key={r.source} source={r.source} finding={r.finding} />
                ))}
              </div>
            </CollapsibleCard>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
